import { endOfDay, parseISO, startOfDay } from 'date-fns';
import api from 'src/utils/ajax';

import { populateRelationshipFromRequest } from '../../../utils/populateRelationship';
import { fetchUsers } from '../../user/user';
import { fetchTasks } from '../task/task';

import {
  denormalizeClockEntry,
  normalizeClockEntry,
  normalizeClockEntryAggregates,
} from './normalizers';

/** @typedef { import('../../user/_types').User } User */
/** @typedef { import('../task/_types').Task } Task */
/** @typedef { import('./_types').ClockEntry } ClockEntry */
/** @typedef { import('./_types').ClockEntryFilters } ClockEntryFilters */
/** @typedef { import('./_types').ClockEntryWithUserAndTask } ClockEntryWithUserAndTask */
/** @typedef { import('./_types').ClockEntryWithUser } ClockEntryWithUser */
/** @typedef { import('./_types').ClockEntryWithTask } ClockEntryWithTask */

/** @type {(task: Partial<ClockEntry>) => Promise<ClockEntry>} */
export const createClockEntry = async (entry) => {
  const response = await api.post(
    `/api/v4/clock/log_entries`,
    denormalizeClockEntry(entry)
  );
  return normalizeClockEntry(response.data);
};

/** @type {(entry: Partial<ClockEntry>) => Promise<ClockEntry>} */
export const updateClockEntry = async (entry) => {
  const response = await api.put(
    `/api/v4/clock/log_entries/${entry.id}`,
    denormalizeClockEntry(entry)
  );
  return normalizeClockEntry(response.data);
};

/** @type {(clockEntry: Partial<ClockEntry>) => Promise<ClockEntry>} */
export const deleteClockEntry = async (clockEntry) => {
  const response = await api.delete(
    `/api/v4/clock/log_entries/${clockEntry.id}`
  );
  return normalizeClockEntry(response.data);
};

/** @type {() => Promise<ClockEntryWithTask[]>} */
export const fetchLatestEntries = async () => {
  const response = await api.get(`/api/v4/clock/log_entries/latest`);
  const entries = response.data?.map((entry) => {
    return normalizeClockEntry(entry);
  });

  if (!entries) {
    return [];
  }

  return addTaskToEntries(entries);
};

/** @typedef {object} FetchEntriesOptions
 * @property {number} page
 * @property {number} perPage
 * @property {string} sort
 * @property {string} sortDir
 * @property {Partial<ClockEntryFilters>} filters
 * @property {boolean} withUserAndTask
 */

/**
 * @typedef {object} FetchEntriesResponse
 * @property {ClockEntryWithUserAndTask[]} entries
 * @property {object} aggregates
 * @property {number} total
 */

/** @type {(options: FetchEntriesOptions) => Promise<FetchEntriesResponse>} */
export const fetchEntries = async ({
  page = 1,
  perPage = 20,
  sort,
  sortDir,
  filters = {},
  withUserAndTask = true,
}) => {
  const response = await api.get(`/api/v4/clock/log_entries`, {
    params: {
      page: page,
      per: perPage,
      q: {
        s: `${sort} ${sortDir}`,
        user_id_eq: filters.trainers?.join(','),
        task_id_eq: filters.tasks?.join(','),
        start_date_gteq:
          typeof filters.startDate === 'string'
            ? startOfDay(parseISO(filters.startDate))
            : filters.startDate,
        end_date_lteq:
          typeof filters.endDate === 'string'
            ? endOfDay(parseISO(filters.endDate))
            : filters.endDate,
      },
    },
  });

  const entries = response.data.log_entry?.map((entry) => {
    return normalizeClockEntry(entry);
  });

  if (!entries) {
    return { entries: [], aggregates: {}, total: 0 };
  }

  return {
    entries: withUserAndTask ? await addUserAndTaskToEntries(entries) : entries,
    aggregates: normalizeClockEntryAggregates(response.data.meta.aggregates),
    total: response.data.meta.total,
  };
};

/** @type {(entries: ClockEntry[]) => Promise<ClockEntryWithUserAndTask[]>} */
async function addUserAndTaskToEntries(entries) {
  const entriesWithUser = await populateRelationshipFromRequest({
    list: entries,
    idKey: 'userId',
    newKey: 'user',
    func: (ids) => fetchUsers({ ids }),
  });

  const entriesWithUserAndTask = await populateRelationshipFromRequest({
    list: entriesWithUser,
    idKey: 'taskId',
    newKey: 'task',
    func: (ids) => fetchTasks({ ids }),
  });

  return entriesWithUserAndTask;
}

/** @type {(entries: ClockEntry[]) => Promise<ClockEntryWithTask[]>} */
async function addTaskToEntries(entries) {
  const entriesWithTask = await populateRelationshipFromRequest({
    list: entries,
    idKey: 'taskId',
    newKey: 'task',
    func: (ids) => fetchTasks({ ids }),
  });

  return entriesWithTask;
}

/** @typedef {object} clockInOptions
 * @property {import('types/utilityTypes').Identifier} taskId
 * @property {import('types/utilityTypes').Identifier} userId
 * @property {string} notes
 */
/** @type {(options: clockInOptions) => Promise<ClockEntry>} */
export async function clockIn({ taskId, userId, notes }) {
  const response = await api.post(`/api/v4/clock/log_entries/clock_in`, {
    task_id: taskId,
    user_id: userId,
    notes,
  });
  return normalizeClockEntry(response.data);
}
/** @type {(user: User, notes: string) => Promise<ClockEntry>} */
export async function clockOut(user, notes) {
  const response = await api.put(`/api/v4/clock/log_entries/clock_out`, {
    user_id: user.id,
    notes,
  });
  return normalizeClockEntry(response.data);
}

/** @type {(user: User) => Promise<ClockEntryWithTask | null>} */
export async function fetchLatestForUser(user) {
  const response = await api.get(`/api/v4/clock/log_entries/latest_for_user`, {
    params: {
      user_id: user.id,
    },
  });
  const { data } = response;

  if (!data) return null;

  const clock = normalizeClockEntry(data);
  const clockWithTask = await populateRelationshipFromRequest({
    list: [clock],
    idKey: 'taskId',
    newKey: 'task',
    func: (ids) => fetchTasks({ ids }),
  });
  return clockWithTask[0];
}
